// src/App.tsx
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Route,
  RouterProvider,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense } from "react";
import { Box, Loader } from "@mantine/core";
import "@mantine/carousel/styles.css";
import "./App.scss";
import { validateSlugLoader } from "@utils/loaders/validateSlugLoader";
import { legacyRegisterLoader } from "@utils/loaders/legacyRegisterLoader";
import TaxProClientList from "./pages/TaxProClientList/TaxProClientList";
import BusinessLogin from "./pages/LoginManagement/pages/BusinessLogin/BusinessLogin";
import Login from "./pages/LoginManagement/pages/Login/Login";
import AlphaSignupPage from "./pages/AlphaSignup/AlphaSignupPage";
import Logout from "./pages/LoginManagement/pages/Logout/Logout";
import CreateAccount from "./pages/CreateAccount/CreateAccount";
import TaxClientHome from "./pages/TaxClientHome/TaxClientHome";
import ErrorPage from "./pages/ErrorPage/ErrorPage";
import Settings from "./pages/Settings/Settings";
import TaxProHome from "./pages/TaxProHome/TaxProHome";
import SmartwizLiteHome from "./pages/SmartwizLiteHome/SmartwizLiteHome";
import ForgotPassword from "./pages/ForgotPassword/ForgotPassword";
import ConfirmResetPassword from "./pages/ConfirmResetPassword/ConfirmResetPassword";
import ManagedBusinessInvitePage from "./pages/ManagedBusinessInvite/ManagedBusinessInvite";
import CustomContractOfferInvitePage from "./pages/CustomContractOfferInvite/CustomContractOfferInvite";
import PaymentSuccess from "./pages/PaymentSuccess/PaymentSuccess";
import TaxProReports from "./pages/TaxProReports/TaxProReports";
import TaxOrganizerHome from "./pages/TaxOrganizer/TaxOrganizerHome/TaxOrganizerHome";
import DemoResources from "./pages/DemoResources/DemoResources";
import Payments from "./pages/Payments/Payments";
import ClientProfile from "./pages/ClientProfile/ClientProfile";
import Root from "./Root";
import { Notifications } from "@mantine/notifications";
import PaymentLoader from "./components/PaymentLoader/PaymentLoader";
import AccountVerification from "./pages/VerificationPage/components/AccountVerificationScreen";
import ClientRedirect from "./pages/CreateAccount/ClientRedirect";
import ProtectedRoute from "./components/ProtectedRoute";
import LoginRedirect from "./components/LoginRedirect/LoginRedirect";
import RootRedirect from "./components/RootRedirect/RootRedirect";
import { AppRoutes } from "@/constants.ts";
import CASLProvider from "./components/CASLProvider";
import { OneClickSessionDebug } from "./pages/admin/OneClickSessionDebug/OneClickSessionDebug";
import { TimestepDetail } from "./pages/admin/TimestepDetail/TimestepDetail";
import { IntegrationLogs } from "./pages/admin/IntegrationLogs";
import { AnalyticsPage } from "./pages/admin/Analytics";
// Import skeleton loader (not lazy loaded to show immediately)
import { OnboardingSkeleton } from "@pages/Onboarding/components/OnboardingSkeleton.tsx";
import ExtensionUninstalled from "./pages/ExtensionUninstalled/ExtensionUninstalled";
import FailedPayment from "./pages/LoginManagement/components/LoginView/FailedPayment";
import ExpiredSubscription from "./pages/LoginManagement/components/LoginView/ExpiredSubscription";
import BillingAccountIssue from "./pages/LoginManagement/components/LoginView/BillingAccountIssue";

// Lazy load only the largest/heaviest components
const TaxOrganizerPage = lazy(
  () => import("./pages/TaxOrganizer/TaxOrganizerPage")
);
const Onboarding = lazy(() => import("./pages/Onboarding/Onboarding"));
const ContractUpgradePage = lazy(
  () => import("./pages/ContractUpgrade/ContractUpgradePage")
);
const ServiceBureauRedirect = lazy(
  () => import("./pages/ServiceBureau/ServiceBureauRedirect")
);

const queryClient = new QueryClient();

const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      {/* Public onboarding route - outside Root to avoid redirects */}
      <Route
        path={AppRoutes.gettingStarted.substring(1)}
        element={
          <Suspense fallback={<OnboardingSkeleton />}>
            <Onboarding />
          </Suspense>
        }
      />
      <Route
        path={AppRoutes.root}
        element={
          <CASLProvider showLoadingScreen={false}>
            <Root />
          </CASLProvider>
        }
      >
        <Route index element={<RootRedirect />} />
        <Route
          path={AppRoutes.home.substring(1)}
          element={
            <ProtectedRoute
              requiredRole={["owner", "team_lead", "team_member"]}
            >
              <TaxProHome />
            </ProtectedRoute>
          }
        />
        <Route
          path={AppRoutes.homeLite.substring(1)}
          element={
            <ProtectedRoute
              requiredRole={["owner", "team_lead", "team_member"]}
            >
              <SmartwizLiteHome />
            </ProtectedRoute>
          }
        />
        <Route
          path={AppRoutes.contractUpgrade.substring(1)}
          element={
            <ProtectedRoute requiredRole="owner">
              <Suspense
                fallback={
                  <Box
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100vh",
                    }}
                  >
                    <Loader size="xl" />
                  </Box>
                }
              >
                <ContractUpgradePage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path={AppRoutes.serviceBureau.substring(1)}
          element={
            <ProtectedRoute
              requiredRole={["owner", "team_lead", "team_member"]}
              requiresServiceBureau
            >
              <Suspense
                fallback={
                  <Box
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100vh",
                    }}
                  >
                    <Loader size="xl" />
                  </Box>
                }
              >
                <ServiceBureauRedirect />
              </Suspense>
            </ProtectedRoute>
          }
        />
        {/* Old TaxProOnboarding route removed - use AppRoutes.gettingStarted instead */}
        <Route
          path={AppRoutes.reports.substring(1)}
          element={<TaxProReports />}
        />
        <Route
          path={AppRoutes.clients.substring(1)}
          element={<TaxProClientList />}
        />
        <Route
          path={AppRoutes.settings.substring(1)}
          element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          }
        />
        <Route
          path={AppRoutes.taxOrganizerHome.substring(1)}
          element={
            <ProtectedRoute requiredRole="client">
              <TaxOrganizerHome />
            </ProtectedRoute>
          }
        />
        <Route
          path={AppRoutes.taxOrganizerYear.substring(1)}
          element={
            <Suspense
              fallback={
                <Box
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                  }}
                >
                  <Loader size="xl" />
                </Box>
              }
            >
              <TaxOrganizerPage />
            </Suspense>
          }
        />
        {/* Specific routes must come before catch-all businessSlug route */}
        <Route
          path=":businessSlug/custom-contract-offer/invite"
          element={<CustomContractOfferInvitePage />}
        />
        <Route
          path={AppRoutes.businessSlug.substring(1)}
          loader={validateSlugLoader}
          errorElement={<ErrorPage />} // 404 when slug invalid
        >
          <Route index element={<LoginRedirect />} />
          <Route
            path={AppRoutes.businessLogin.substring(1)}
            element={<BusinessLogin />}
          />
          <Route
            path={AppRoutes.businessRegister.substring(1)}
            loader={legacyRegisterLoader}
            element={<CreateAccount />}
          />
          <Route
            path={AppRoutes.businessForgotPassword.substring(1)}
            element={<ForgotPassword />}
          />
          <Route
            path={AppRoutes.businessResetPasswordConfirm.substring(1)}
            element={<ConfirmResetPassword />}
          />
          <Route
            path={AppRoutes.businessManagedBusinessInvite.substring(1)}
            element={<ManagedBusinessInvitePage />}
          />
          <Route
            path={AppRoutes.businessCustomContractOfferInvite.substring(1)}
            element={<CustomContractOfferInvitePage />}
          />
        </Route>
        <Route
          path={AppRoutes.clientHome.substring(1)}
          element={<TaxClientHome />}
        />
        <Route
          path={AppRoutes.forgotPassword.substring(1)}
          element={<ForgotPassword />}
        />
        <Route
          path={AppRoutes.resetPasswordConfirm.substring(1)}
          element={<ConfirmResetPassword />}
        />
        <Route
          path={AppRoutes.register.substring(1)}
          element={<ClientRedirect />}
        />
        <Route
          path={AppRoutes.registerPro.substring(1)}
          element={
            <Navigate
              to={AppRoutes.gettingStarted + window.location.search}
              replace
            />
          }
        />
        {/* Old LiteTrialOnboard route removed - integrated into main onboarding */}
        <Route
          path={AppRoutes.registerPayment.substring(1)}
          element={<PaymentLoader />}
        />
        <Route
          path={AppRoutes.success.substring(1)}
          element={<PaymentSuccess />}
        />
        {/* Alpha group signup (public, no auth required) */}
        <Route path={AppRoutes.alphaSignup} element={<AlphaSignupPage />} />
        {/* Global login route (no business context) */}
        <Route path={AppRoutes.login} element={<Login />} />
        <Route path={AppRoutes.logout.substring(1)} element={<Logout />} />
        <Route
          path={AppRoutes.paymentUpdate.substring(1)}
          element={<FailedPayment />}
        />
        <Route
          path={AppRoutes.subscriptionExpired.substring(1)}
          element={<ExpiredSubscription email="" />}
        />
        <Route
          path={AppRoutes.billingAccountIssue.substring(1)}
          element={<BillingAccountIssue />}
        />
        <Route
          path={AppRoutes.verify.substring(1)}
          element={<AccountVerification />}
        />
        <Route
          path={AppRoutes.resources.substring(1)}
          element={<DemoResources />}
        />

        <Route
          path={AppRoutes.clientProfile.substring(1)}
          element={<ClientProfile />}
        />

        {/* Admin debug pages */}
        <Route
          path={AppRoutes.adminReturnDebug.substring(1)}
          element={
            <ProtectedRoute>
              <OneClickSessionDebug />
            </ProtectedRoute>
          }
        />
        <Route
          path={AppRoutes.adminTimestepDetail.substring(1)}
          element={
            <ProtectedRoute>
              <TimestepDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path={AppRoutes.adminIntegrationLogs.substring(1)}
          element={
            <ProtectedRoute>
              <IntegrationLogs />
            </ProtectedRoute>
          }
        />
        <Route
          path={AppRoutes.adminAnalytics.substring(1)}
          element={
            <ProtectedRoute>
              <AnalyticsPage />
            </ProtectedRoute>
          }
        />

        <Route path={AppRoutes.payments.substring(1)} element={<Payments />} />

        {/* Extension uninstall survey - public route */}
        <Route
          path={AppRoutes.extensionUninstalled.substring(1)}
          element={<ExtensionUninstalled />}
        />

        {/* redirect routes */}
        <Route path={AppRoutes.notFound} element={<ErrorPage />} />
      </Route>
    </>
  )
);

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <Notifications zIndex={10000} position="bottom-right" />
    </QueryClientProvider>
  );
}

export default App;
